import React, { useState, useRef, useEffect } from 'react';
import '../styles/Modal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faMagnifyingGlassPlus, faMagnifyingGlassMinus, faSyncAlt } from '@fortawesome/free-solid-svg-icons';

const Modal = ({ image, altText, onClose }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);

  // Fonctions de zoom et rotation
  const handleZoomIn = () => setZoomLevel(prevZoomLevel => Math.min(prevZoomLevel + 0.1, 3));
  const handleZoomOut = () => setZoomLevel(prevZoomLevel => Math.max(prevZoomLevel - 0.1, 0.5));
  const handleReset = () => {
    setZoomLevel(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };
  const handleRotate = () => setRotation(prevRotation => (prevRotation + 90) % 360);

  // Gestion des événements tactiles pour le défilement
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setStartPos({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    e.preventDefault();
    setPosition({
      x: e.touches[0].clientX - startPos.x,
      y: e.touches[0].clientY - startPos.y,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Gestion des événements tactiles pour le zoom (pincement)
  const handlePinchZoom = (e) => {
    if (e.touches.length !== 2) return;
    e.preventDefault();

    const touch1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    const touch2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };

    const currentDistance = Math.hypot(
      touch1.x - touch2.x,
      touch1.y - touch2.y
    );

    if (!initialDistanceRef.current) {
      initialDistanceRef.current = currentDistance;
      return;
    }

    const newZoomLevel = zoomLevel * (currentDistance / initialDistanceRef.current);
    setZoomLevel(Math.min(Math.max(newZoomLevel, 0.5), 3));
    initialDistanceRef.current = currentDistance;
  };

  const initialDistanceRef = useRef(null);

  // Réinitialisation des distances au démontage du composant
  useEffect(() => {
    return () => {
      initialDistanceRef.current = null;
    };
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose} onTouchEnd={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="modal-image-container"
          onTouchMove={handlePinchZoom}
          style={{
            transform: `scale(${zoomLevel}) rotate(${rotation}deg) translate(${position.x}px, ${position.y}px)`,
            transformOrigin: 'center',
            transition: isDragging ? 'none' : 'transform 0.3s ease',
          }}
        >
          <img
            src={image}
            alt={altText}
            className="modal-image"
            ref={imageRef}
          />
        </div>
        <button className="modal-close-button" onClick={onClose} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div className="modal-controls">
          <button className="control-button" onClick={handleZoomIn} aria-label="Zoom In">
            <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
          </button>
          <button className="control-button" onClick={handleZoomOut} aria-label="Zoom Out">
            <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
          </button>
          <button className="control-button" onClick={handleReset} aria-label="Reset">
            Reset
          </button>
          <button className="control-button" onClick={handleRotate} aria-label="Rotate">
            <FontAwesomeIcon icon={faSyncAlt} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Modal;
